import { IcoArrowLeft, IcoClose } from "assets/icons";
import { BaseModal, Button, ModalFooter, RadioInput, TextArea } from "components";
import { ErrorHelper } from "helpers";
import { useIsMobile } from "hooks/useIsMobile";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { ApplicationService } from "services/applicationService";
import { CancellationReason } from "services/types";

type Props = {
	applicationId: string;
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: () => void;
};

export default function CancelApprovedApplicationModal({ applicationId, isOpen, onClose, onSuccess }: Props) {
	const isMobile = useIsMobile();
	const applicationService = useMemo(ApplicationService.getInstance, []);
	const [reasons, setReasons] = useState<CancellationReason[]>([]);
	const [selectedReason, setSelectedReason] = useState<string>("");
	const [description, setDescription] = useState<string>("");
	const [loading, setLoading] = useState<boolean>(false);
	const isOther = useMemo(() => {
		const selected = reasons.find((r) => r.id === selectedReason);
		if (!selected) return false;
		return selected.name.toLowerCase().trim() === "outro";
	}, [reasons, selectedReason]);

	useEffect(() => {
		if (!isOpen) return;
		setSelectedReason("");
		setDescription("");
		applicationService
			.listCancellationReasons()
			.then((list) => setReasons(list))
			.catch((err) => toast.error(ErrorHelper.getResponseErrorMessage(err)));
	}, [isOpen, applicationId, applicationService]);

	const MIN_OTHER_LENGTH = 50;

	const handleConfirm = async () => {
		if (!selectedReason) {
			toast.warning("Selecione um motivo para continuar");
			return;
		}
		if (isOther && description.trim().length < MIN_OTHER_LENGTH) {
			toast.warning(`Descreva o motivo com no mínimo ${MIN_OTHER_LENGTH} caracteres`);
			return;
		}
		setLoading(true);
		try {
			const selected = reasons.find((r) => r.id === selectedReason);
			const eventDescription = isOther ? description.trim() : selected?.name || "";
			const otherText = isOther ? description.trim() : undefined;
			await applicationService.cancelApprovedApplication(applicationId, selectedReason, eventDescription, otherText);
			toast.success("Isenção cancelada com sucesso");
			onClose();
			onSuccess?.();
		} catch (error) {
			toast.error(ErrorHelper.getResponseErrorMessage(error));
		} finally {
			setLoading(false);
		}
	};

	return (
		<BaseModal onClickOutsideModal={onClose} isOpen={isOpen} contentClassName="rounded-xl overflow-hidden" bodyClassName="!px-6 !py-6 !gap-4">
			{isMobile && (
				<div className="flex justify-start px-6 py-2 border-b-2 border-neutral-high-100">
					<button onClick={onClose} aria-label="voltar">
						<IcoArrowLeft size="24" />
					</button>
				</div>
			)}

			<div className="flex justify-between items-center px-1">
				<div className="font-heading-semibold-tiny">Cancelar isenção</div>
				{!isMobile && (
					<Button kind="icon" hierarchy="ghost" onClick={onClose}>
						<IcoClose />
					</Button>
				)}
			</div>

			<div className="flex flex-col gap-6 px-1 overflow-y-auto">
				<div className="font-neutral-low-400">
					Esta é uma operação irreversível e não será possível desfazê-la posteriormente. Indique o motivo do cancelamento:
				</div>

				<RadioInput
					name="cancellationReason"
					value={selectedReason}
					onChange={(e: ChangeEvent<HTMLInputElement>) => setSelectedReason(e.target.value)}
					options={reasons.map((r) => ({ label: r.name, value: r.id }))}
					isRequired={false}
					layout="vertical"
					touched
				/>

				{isOther && (
					<TextArea
						label="Outro motivo"
						placeholder="Digite"
						value={description}
						onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
						maxLength={150}
						required
						minLengthText={`Sua avaliação deve possuir no min. ${MIN_OTHER_LENGTH} caracteres`}
						showIncreasingCharCount
						touched
					/>
				)}

				<ModalFooter>
					<Button hierarchy="secondary" size="medium" onClick={onClose}>
						Fechar
					</Button>
					<Button
						hierarchy="primary"
						size="medium"
						onClick={handleConfirm}
						disabled={!selectedReason || (isOther && description.trim().length < MIN_OTHER_LENGTH)}
						isLoading={loading}
					>
						Cancelar isenção
					</Button>
				</ModalFooter>
			</div>
		</BaseModal>
	);
}
